// File: frontend/src/components/Calendar/AppointmentDetailsPopup.js
// Version: 3.0 (quick-260915-kal — зашитые цвета убраны, цвет держит CSS)
// Description: Executive Legal Style details modal matching oslawdash (manus.space).
// Displays full Google Calendar event information: Client Name, Topic, Time, Meet link, Description.

import React from 'react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { 
    X, 
    Calendar as CalendarIcon, 
    Clock, 
    Video, 
    User, 
    Mail, 
    Phone, 
    FileText, 
    ExternalLink 
} from 'lucide-react';
import './AppointmentDetailsPopup.css';

const AppointmentDetailsPopup = ({ isOpen, onClose, event, eventData, onEventUpdated }) => {
    const data = eventData || event;
    if (!isOpen || !data) return null;

    const title = data.summary || data.title || '(Без названия)';
    const startTime = data.start?.dateTime || data.start?.date || data.start;
    const endTime = data.end?.dateTime || data.end?.date || data.end;
    const googleMeetLink = data.hangoutLink || data.raw?.hangoutLink;
    const googleCalendarLink = data.htmlLink || data.raw?.htmlLink;
    const description = data.description || data.raw?.description || '';

    // Extract lead or client details
    const leadDetails = data.lead_details || data.raw?.lead_details;
    let clientName = data.clientName || data.client_name || leadDetails?.full_name || '';
    let clientEmail = leadDetails?.email || '';
    let clientPhone = leadDetails?.phone || '';
    let topic = leadDetails?.topic || '';

    // If description has formatted text like "Client: ... Phone: ... Topic: ...", parse it
    if (description) {
        const lines = description.split('\n');
        for (const line of lines) {
            const l = line.trim();
            if (l.toLowerCase().startsWith('client:') && !clientName) {
                clientName = l.substring(7).trim();
            } else if (l.toLowerCase().startsWith('email:') && !clientEmail) {
                clientEmail = l.substring(6).trim();
            } else if (l.toLowerCase().startsWith('phone:') && !clientPhone) {
                clientPhone = l.substring(6).trim();
            } else if (l.toLowerCase().startsWith('topic:') && !topic) {
                topic = l.substring(6).trim();
            }
        }
    }

    // Fallback if title has format "Consultation: Ekaterina Sukhina"
    if (!clientName && title.includes(':')) {
        clientName = title.split(':')[1].trim();
    }

    const formatDateTime = (val) => {
        if (!val) return '';
        try {
            const d = typeof val === 'string' ? new Date(val) : val;
            return format(d, 'EEEE, d MMMM yyyy · HH:mm', { locale: ru });
        } catch {
            return String(val);
        }
    };

    const formatTimeOnly = (val) => {
        if (!val) return '';
        try {
            const d = typeof val === 'string' ? new Date(val) : val;
            return format(d, 'HH:mm');
        } catch {
            return '';
        }
    };

    return (
        <div className="cal-modal-overlay" onClick={onClose}>
            <div className="cal-modal-container" onClick={(e) => e.stopPropagation()}>
                
                {/* Modal Header */}
                <div className="cal-modal-header">
                    <div>
                        <span className="cal-modal-tag">ДЕТАЛИ КОНСУЛЬТАЦИИ</span>
                        <h3 className="cal-modal-title">{title}</h3>
                    </div>
                    <button type="button" className="cal-modal-close-btn" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                {/* Modal Body */}
                <div className="cal-modal-body">
                    
                    {/* Date & Time Row */}
                    <div className="cal-modal-info-row">
                        <div className="cal-modal-icon-col">
                            <Clock size={16} className="cal-ink-gold" />
                        </div>
                        <div className="cal-modal-text-col">
                            <div className="cal-modal-label">Дата и время</div>
                            <div className="cal-modal-val font-semibold">
                                {formatDateTime(startTime)}
                                {endTime ? ` – ${formatTimeOnly(endTime)}` : ''}
                            </div>
                        </div>
                    </div>

                    {/* Client Information Card */}
                    <div className="cal-modal-client-card">
                        <div className="cal-modal-client-head">
                            <User size={15} />
                            <span>Информация о клиенте</span>
                        </div>
                        <div className="cal-modal-client-grid">
                            <div className="cal-modal-client-field">
                                <span className="cal-field-lbl">Клиент</span>
                                <span className="cal-field-val font-bold">{clientName || 'Не указан'}</span>
                            </div>
                            {topic && (
                                <div className="cal-modal-client-field">
                                    <span className="cal-field-lbl">Тема / Направление</span>
                                    <span className="cal-field-val font-semibold cal-ink-gold">{topic}</span>
                                </div>
                            )}
                            {clientPhone && (
                                <div className="cal-modal-client-field">
                                    <span className="cal-field-lbl">Телефон</span>
                                    <a href={`tel:${clientPhone}`} className="cal-field-link">
                                        <Phone size={12} className="inline mr-1" />
                                        {clientPhone}
                                    </a>
                                </div>
                            )}
                            {clientEmail && (
                                <div className="cal-modal-client-field">
                                    <span className="cal-field-lbl">Email</span>
                                    <a href={`mailto:${clientEmail}`} className="cal-field-link">
                                        <Mail size={12} className="inline mr-1" />
                                        {clientEmail}
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Google Meet Link if exists */}
                    {googleMeetLink && (
                        <div className="cal-modal-meet-box">
                            <div className="cal-meet-left">
                                <Video size={18} className="cal-ink-green" />
                                <div>
                                    <div className="font-bold text-xs text-foreground">Google Meet видеовстреча</div>
                                    <div className="text-[11px] cal-ink-muted">Ссылка для подключения клиента и адвоката</div>
                                </div>
                            </div>
                            <a 
                                href={googleMeetLink} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="cal-meet-btn"
                            >
                                Подключиться
                                <ExternalLink size={12} />
                            </a>
                        </div>
                    )}

                    {/* Description Notes if exist */}
                    {description && (
                        <div className="cal-modal-notes-box">
                            <div className="cal-notes-label">
                                <FileText size={14} />
                                <span>Заметки / Описание</span>
                            </div>
                            <pre className="cal-notes-text">{description}</pre>
                        </div>
                    )}

                    {/* External Google Calendar Link */}
                    {googleCalendarLink && (
                        <div className="cal-modal-footer-link">
                            <a 
                                href={googleCalendarLink} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="cal-gcal-ext-link"
                            >
                                <CalendarIcon size={14} />
                                <span>Открыть в Google Calendar</span>
                                <ExternalLink size={12} />
                            </a>
                        </div>
                    )}

                </div>

                {/* Modal Footer */}
                <div className="cal-modal-footer">
                    <button type="button" className="cal-modal-btn-close" onClick={onClose}>
                        Закрыть
                    </button>
                </div>

            </div>
        </div>
    );
};

export default AppointmentDetailsPopup;
